import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface MediaItem {
  url: string;
  type: string;
}

interface MediaGalleryProps {
  media: MediaItem[];
}

export default function MediaGallery({ media }: MediaGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imageLoading, setImageLoading] = useState<{ [key: number]: boolean }>({});
  const [imageLoaded, setImageLoaded] = useState<{ [key: number]: boolean }>({});

  if (!media || media.length === 0) return null;

  const images = media.filter(m => m.type === 'image' || m.type.startsWith('image/'));
  const videos = media.filter(m => m.type === 'video' || m.type.startsWith('video/'));

  const nextSlide = () => {
    const newIndex = (currentSlide + 1) % images.length;
    setCurrentSlide(newIndex);
    // Mark next image as loading if not already loaded
    if (!imageLoaded[newIndex]) {
      setImageLoading(prev => ({ ...prev, [newIndex]: true }));
    }
  };

  const prevSlide = () => {
    const newIndex = (currentSlide - 1 + images.length) % images.length;
    setCurrentSlide(newIndex);
    // Mark previous image as loading if not already loaded
    if (!imageLoaded[newIndex]) {
      setImageLoading(prev => ({ ...prev, [newIndex]: true }));
    }
  };

  // Preload adjacent images for smoother navigation
  useEffect(() => {
    if (images.length > 1) {
      const nextIndex = (currentSlide + 1) % images.length;
      const prevIndex = (currentSlide - 1 + images.length) % images.length;

      // Preload next and previous images
      [nextIndex, prevIndex].forEach(index => {
        const img = new Image();
        img.src = images[index].url;
      });
    }
  }, [currentSlide, images]);

  // Single image - show full size (with optional videos below)
  if (images.length === 1) {
    return (
      <>
        <div className="mt-3 relative">
          {imageLoading[0] && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-trust-turquoise"></div>
            </div>
          )}
          <img
            src={images[0].url}
            alt="Post content"
            className={`w-full rounded-lg object-cover max-h-96 cursor-pointer transition-opacity duration-300 ${
              imageLoaded[0] ? 'opacity-100' : 'opacity-0'
            }`}
            onLoadStart={() => setImageLoading(prev => ({ ...prev, 0: true }))}
            onLoad={() => {
              setImageLoading(prev => ({ ...prev, 0: false }));
              setImageLoaded(prev => ({ ...prev, 0: true }));
            }}
            onClick={() => setSelectedIndex(0)}
          />
        </div>

        {/* Lightbox modal for single image */}
        {selectedIndex !== null && createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center p-4"
            onClick={() => setSelectedIndex(null)}
          >
            <button
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-[10000] bg-black/60 rounded-full p-2"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedIndex(null);
              }}
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <img
              src={images[0].url}
              alt="Post content"
              className="max-w-full max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>,
          document.body
        )}

        {/* Videos below single image */}
        {videos.length > 0 && (
          <div className="mt-3 space-y-2">
            {videos.map((video, index) => (
              <video
                key={index}
                src={video.url}
                controls
                className="w-full rounded-lg max-h-96"
              />
            ))}
          </div>
        )}
      </>
    );
  }

  // Multiple images - show carousel (with optional videos below)
  if (images.length > 1) {
    return (
      <>
        <div className="mt-3">
          <div className="relative group">
            {/* Carousel container */}
            <div className="relative rounded-lg overflow-hidden">
              {imageLoading[currentSlide] && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 z-10">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-trust-turquoise"></div>
                </div>
              )}
              <img
                src={images[currentSlide].url}
                alt={`Image ${currentSlide + 1}`}
                className={`w-full h-96 object-cover cursor-pointer transition-opacity duration-300 ${
                  imageLoaded[currentSlide] ? 'opacity-100' : 'opacity-0'
                }`}
                onLoadStart={() => setImageLoading(prev => ({ ...prev, [currentSlide]: true }))}
                onLoad={() => {
                  setImageLoading(prev => ({ ...prev, [currentSlide]: false }));
                  setImageLoaded(prev => ({ ...prev, [currentSlide]: true }));
                }}
                onClick={() => setSelectedIndex(currentSlide)}
              />

              {/* Navigation buttons - only show on hover */}
              {images.length > 1 && (
                <>
                  <button
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      prevSlide();
                    }}
                    aria-label="Previous image"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      nextSlide();
                    }}
                    aria-label="Next image"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              {/* Slide indicator */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                {currentSlide + 1} / {images.length}
              </div>
            </div>

            {/* Dots indicator */}
            <div className="flex justify-center mt-2 gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentSlide
                      ? 'bg-trust-turquoise w-4'
                      : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Lightbox modal */}
        {selectedIndex !== null && createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center p-4"
            onClick={() => setSelectedIndex(null)}
          >
            <button
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-[10000] bg-black/60 rounded-full p-2"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedIndex(null);
              }}
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors bg-black/60 rounded-full p-3 z-[10000]"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedIndex((prev) => {
                  if (prev !== null) {
                    const newIndex = prev > 0 ? prev - 1 : images.length - 1;
                    if (!imageLoaded[newIndex]) {
                      setImageLoading(prevLoading => ({ ...prevLoading, [newIndex]: true }));
                    }
                    return newIndex;
                  }
                  return null;
                });
              }}
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors bg-black/60 rounded-full p-3 z-[10000]"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedIndex((prev) => {
                  if (prev !== null) {
                    const newIndex = prev < images.length - 1 ? prev + 1 : 0;
                    if (!imageLoaded[newIndex]) {
                      setImageLoading(prevLoading => ({ ...prevLoading, [newIndex]: true }));
                    }
                    return newIndex;
                  }
                  return null;
                });
              }}
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {imageLoading[selectedIndex] && (
              <div className="absolute inset-0 flex items-center justify-center z-[9998]">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
              </div>
            )}
            <img
              src={images[selectedIndex].url}
              alt={`Image ${selectedIndex + 1}`}
              className={`max-w-full max-h-[90vh] object-contain transition-opacity duration-300 ${
                imageLoaded[selectedIndex] ? 'opacity-100' : 'opacity-0'
              }`}
              onLoadStart={() => setImageLoading(prev => ({ ...prev, [selectedIndex]: true }))}
              onLoad={() => {
                setImageLoading(prev => ({ ...prev, [selectedIndex]: false }));
                setImageLoaded(prev => ({ ...prev, [selectedIndex]: true }));
              }}
              onClick={(e) => e.stopPropagation()}
            />

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black/60 px-4 py-2 rounded-full z-[10000]">
              {selectedIndex + 1} / {images.length}
            </div>
          </div>,
          document.body
        )}

        {/* Videos below carousel */}
        {videos.length > 0 && (
          <div className="mt-3 space-y-2">
            {videos.map((video, index) => (
              <video
                key={index}
                src={video.url}
                controls
                className="w-full rounded-lg max-h-96"
              />
            ))}
          </div>
        )}
      </>
    );
  }

  // Videos only (no images)
  if (videos.length > 0 && images.length === 0) {
    return (
      <div className="mt-3 space-y-2">
        {videos.map((video, index) => (
          <video
            key={index}
            src={video.url}
            controls
            className="w-full rounded-lg max-h-96"
          />
        ))}
      </div>
    );
  }

  return null;
}
